import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  bottleMilkTypeForEvent,
  fetchBabyEvents,
  formatFeedingAmount,
  normalizeFeedingType,
} from "./BabyTrackerShared";
import BabyTrackerLoading from "./BabyTrackerLoading";

const windowHours = 48;
const windowMs = windowHours * 60 * 60 * 1000;
const overlayWindows = [
  { key: "last48", label: "Last 48h", startOffset: 48, endOffset: 0 },
  { key: "previous48", label: "48-96h ago", startOffset: 96, endOffset: 48 },
  { key: "older48", label: "96-144h ago", startOffset: 144, endOffset: 96 },
];

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "No data";
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
};

const formatTime = (date) => date.toLocaleTimeString([], {
  hour: "numeric",
  minute: "2-digit",
});

const feedingDescription = (entry) => {
  const feedingType = normalizeFeedingType(entry.eventType);
  if (feedingType === "Bottle") {
    const amount = entry.feedingAmount ? `${entry.feedingAmount} ${entry.feedingUnit}` : "Bottle";
    return `${amount} ${bottleMilkTypeForEvent(entry)}`;
  }
  const amount = entry.feedingAmount ? formatFeedingAmount(entry) : "Breast";
  return entry.feedingSide ? `${amount}, started ${entry.feedingSide}` : amount;
};

const BabyTrackerFeedingChart = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [selectedWindowKeys, setSelectedWindowKeys] = useState(["last48"]);

  useEffect(() => {
    const capturedNow = new Date();
    setNow(capturedNow);
    fetchBabyEvents("feeding", 400)
      .then((data) => {
        setEvents(data || []);
        setLoading(false);
      })
      .catch(() => {
        setEvents([]);
        setLoading(false);
      });
  }, []);

  const chartData = useMemo(() => {
    const allFeedings = events
      .map((entry) => ({
        ...entry,
        occurredDate: new Date(entry.occurredAtInput),
      }))
      .filter((entry) => !Number.isNaN(entry.occurredDate.getTime()))
      .sort((left, right) => left.occurredDate - right.occurredDate);

    const windows = overlayWindows.map((windowConfig) => {
      const windowStart = new Date(now.getTime() - windowConfig.startOffset * 60 * 60 * 1000);
      const windowEnd = new Date(now.getTime() - windowConfig.endOffset * 60 * 60 * 1000);
      const feedings = allFeedings
        .filter((entry) => entry.occurredDate >= windowStart && entry.occurredDate <= windowEnd)
        .map((entry, index) => ({
          ...entry,
          overlayWindowKey: windowConfig.key,
          overlayWindowLabel: windowConfig.label,
          chartPosition: ((entry.occurredDate - windowStart) / windowMs) * 100,
          markerIndex: index,
        }));
      return {
        ...windowConfig,
        windowStart,
        windowEnd,
        feedings,
      };
    });

    const currentWindow = windows[0];
    const feedingsWithIntervals = currentWindow.feedings.map((entry, index) => {
      const previous = currentWindow.feedings[index - 1];
      return {
        ...entry,
        minutesSincePrevious: previous
          ? Math.round((entry.occurredDate - previous.occurredDate) / 60000)
          : null,
      };
    });

    const lastFeeding = feedingsWithIntervals[feedingsWithIntervals.length - 1] || null;
    const currentGapMinutes = lastFeeding
      ? Math.round((now - lastFeeding.occurredDate) / 60000)
      : null;
    const completedIntervals = feedingsWithIntervals
      .map((entry) => entry.minutesSincePrevious)
      .filter((minutes) => minutes !== null);
    const longestGapMinutes = completedIntervals.length ? Math.max(...completedIntervals) : null;

    return {
      windows,
      selectedFeedings: windows
        .filter((windowConfig) => selectedWindowKeys.includes(windowConfig.key))
        .flatMap((windowConfig) => windowConfig.feedings),
      feedings: feedingsWithIntervals,
      lastFeeding,
      currentGapMinutes,
      longestGapMinutes,
    };
  }, [events, now, selectedWindowKeys]);

  const axisLabels = [
    "Start",
    "+12h",
    "+24h",
    "+36h",
    "+48h",
  ];

  const toggleWindow = (windowKey) => {
    setSelectedWindowKeys((currentKeys) => {
      if (currentKeys.includes(windowKey)) {
        return currentKeys.length === 1 ? currentKeys : currentKeys.filter((key) => key !== windowKey);
      }
      return [...currentKeys, windowKey];
    });
  };

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">Feeding timeline</h2>
          <p className="BabyTrackerSubtitle">
            Feedings over selectable 48-hour windows, overlaid for comparison.
          </p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Planning window</h3>
        <div className="BabyTrackerMetricGrid">
          <div className="BabyTrackerMetric">
            <span>Since last feed</span>
            <strong>{formatDuration(chartData.currentGapMinutes)}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Last fed</span>
            <strong>{chartData.lastFeeding ? formatTime(chartData.lastFeeding.occurredDate) : "None"}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Feeds</span>
            <strong>{chartData.feedings.length}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Longest gap</span>
            <strong>{formatDuration(chartData.longestGapMinutes)}</strong>
          </div>
        </div>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <div className="BabyTrackerHistoryHeader">
          <h3>48-hour overlay</h3>
          <div className="BabyTrackerWindowToggleRow">
            {overlayWindows.map((windowConfig) => (
              <label className={`BabyTrackerWindowToggle BabyTrackerWindowToggle-${windowConfig.key}`} key={windowConfig.key}>
                <input
                  checked={selectedWindowKeys.includes(windowConfig.key)}
                  onChange={() => toggleWindow(windowConfig.key)}
                  type="checkbox"
                />
                {windowConfig.label}
              </label>
            ))}
          </div>
        </div>
        {loading ? <BabyTrackerLoading label="Loading feeding timeline" /> : null}
        {!loading && !chartData.selectedFeedings.length ? <p>No feedings logged in the selected windows.</p> : null}
        {chartData.selectedFeedings.length ? (
          <div className="BabyTrackerTimeline BabyTrackerTimelineOverlay" aria-label="Feeding timeline over selected 48 hour windows">
            <div className="BabyTrackerTimelineTrack"></div>
            {chartData.selectedFeedings.map((entry, index) => (
              <div
                className={`BabyTrackerTimelineMarker BabyTrackerTimelineMarker-${entry.overlayWindowKey}`}
                key={`${entry.overlayWindowKey}-${entry.id}`}
                style={{
                  left: `${entry.chartPosition}%`,
                  top: `${28 + overlayWindows.findIndex((windowConfig) => windowConfig.key === entry.overlayWindowKey) * 30 + (entry.markerIndex % 2) * 9}px`,
                }}
                title={`${entry.overlayWindowLabel}: ${formatTime(entry.occurredDate)} - ${feedingDescription(entry)}`}
              >
                <span className="BabyTrackerTimelineDot"></span>
                <span className="BabyTrackerTimelineLabel">{formatTime(entry.occurredDate)}</span>
              </div>
            ))}
            <div className="BabyTrackerTimelineAxis">
              {axisLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Feeding intervals</h3>
        {chartData.feedings.length ? (
          [...chartData.feedings].reverse().map((entry) => (
            <div className="BabyTrackerHistoryRow" key={entry.id}>
              <div>
                <strong>{formatTime(entry.occurredDate)}</strong>
                <div>{feedingDescription(entry)}</div>
              </div>
              <span>
                {entry.minutesSincePrevious === null
                  ? "First in window"
                  : `${formatDuration(entry.minutesSincePrevious)} after previous`}
              </span>
            </div>
          ))
        ) : (
          <p>No intervals to show yet.</p>
        )}
      </div>
    </div>
  );
};

export default BabyTrackerFeedingChart;
